/**
 * Waveform Audio Player Component
 * Displays scrambled audio preview with interactive waveform visualization
 */

interface AudioPreview {
  status: 'pending' | 'processing' | 'ready' | 'failed' | 'none';
  url?: string;
  waveformUrl?: string;
  duration?: number;
}

/**
 * Render the waveform audio player
 * Always shows a teaser to download the app
 */
export function renderWaveformPlayer(
  audioPreview: AudioPreview,
  container: HTMLElement
): void {
  // Determine messaging based on status
  const hasAudio = audioPreview.status === 'ready';
  const isProcessing = audioPreview.status === 'pending' || audioPreview.status === 'processing';

  // Show teaser to download app for all statuses
  const duration = audioPreview.duration || 0;
  const durationText = duration > 0 ? formatTime(duration) : '';

  // Customize messaging based on status
  const title = hasAudio ? 'Audio Recording' : 'Listen in the App';
  const ctaText = hasAudio
    ? 'Download Ecstatic Oracle to listen to the full audio recording'
    : isProcessing
      ? 'Audio is processing... Download the app to listen when ready'
      : 'Download Ecstatic Oracle to record and explore your sessions';

  container.innerHTML = `
    <div class="card">
      <h3 class="card-title">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M9 18V5l12-2v13"></path>
          <circle cx="6" cy="18" r="3"></circle>
          <circle cx="18" cy="16" r="3"></circle>
        </svg>
        ${title}
      </h3>

      <div class="waveform-container" id="waveform-container" style="position: relative;">
        <canvas id="waveform-canvas" style="width: 100%; height: 100%; opacity: 0.5;"></canvas>
        <!-- Overlay with play button -->
        <div style="
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.4);
          backdrop-filter: blur(2px);
        ">
          <button id="play-btn-teaser" style="
            width: 64px;
            height: 64px;
            min-width: 64px;
            min-height: 64px;
            margin-bottom: 12px;
            background: linear-gradient(135deg, var(--color-gold), var(--color-accent));
            box-shadow: 0 4px 20px rgba(212, 165, 116, 0.4);
            border: none;
            border-radius: 50%;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: transform 0.2s, box-shadow 0.2s;
          " aria-label="Play in App">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
              <polygon points="5 3 19 12 5 21 5 3"></polygon>
            </svg>
          </button>
          ${durationText ? `<span style="font-size: 0.75rem; color: var(--color-text-secondary);">${durationText}</span>` : ''}
        </div>
      </div>

      <div style="margin-top: 16px; text-align: center;">
        <p style="color: var(--color-text-secondary); font-size: 0.8125rem; margin-bottom: 12px;">
          ${ctaText}
        </p>
        <div style="display: flex; justify-content: center; gap: 12px; flex-wrap: wrap;">
          <a href="#" class="store-button" style="
            padding: 8px 16px;
            background: var(--color-bg-card);
            border: 1px solid var(--color-gold);
            border-radius: 8px;
            color: var(--color-gold-light);
            text-decoration: none;
            font-size: 0.8125rem;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            transition: all 0.2s;
          ">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            App Store
          </a>
          <a href="#" class="store-button" style="
            padding: 8px 16px;
            background: var(--color-bg-card);
            border: 1px solid var(--color-gold);
            border-radius: 8px;
            color: var(--color-gold-light);
            text-decoration: none;
            font-size: 0.8125rem;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            transition: all 0.2s;
          ">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z"/>
            </svg>
            Google Play
          </a>
        </div>
      </div>
    </div>
  `;

  // Draw placeholder waveform
  const canvas = document.getElementById('waveform-canvas') as HTMLCanvasElement;
  if (canvas) {
    drawWaveform(canvas, generatePlaceholderPeaks(100));
  }

  // Add click handler for teaser play button - show download popup
  const playBtnTeaser = document.getElementById('play-btn-teaser');
  playBtnTeaser?.addEventListener('click', () => {
    showDownloadPopup(hasAudio);
  });
}

/**
 * Show download popup modal
 */
function showDownloadPopup(hasAudio: boolean): void {
  // Remove existing popup if any
  const existingPopup = document.getElementById('download-popup');
  if (existingPopup) {
    existingPopup.remove();
  }

  const title = hasAudio ? 'Listen in the App' : 'Get the App';
  const message = hasAudio
    ? 'Download Ecstatic Oracle to listen to this recording and explore more sessions'
    : 'Download Ecstatic Oracle to record your own sessions and explore the quantum realm';

  const popup = document.createElement('div');
  popup.id = 'download-popup';
  popup.innerHTML = `
    <div class="popup-overlay" style="
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      backdrop-filter: blur(8px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      animation: fadeIn 0.2s ease;
    ">
      <div class="popup-content" style="
        background: linear-gradient(135deg, var(--color-bg-secondary), var(--color-bg-card));
        border: 1px solid var(--color-gold);
        border-radius: 20px;
        padding: 32px;
        max-width: 380px;
        width: 90%;
        text-align: center;
        position: relative;
        animation: slideUp 0.3s ease;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(212, 165, 116, 0.2);
      ">
        <button id="popup-close" style="
          position: absolute;
          top: 12px;
          right: 12px;
          background: none;
          border: none;
          color: var(--color-text-secondary);
          cursor: pointer;
          padding: 8px;
          font-size: 20px;
          line-height: 1;
          transition: color 0.2s;
        " aria-label="Close">&times;</button>

        <div style="
          width: 80px;
          height: 80px;
          margin: 0 auto 20px;
          background: linear-gradient(135deg, var(--color-gold), var(--color-accent));
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 24px rgba(212, 165, 116, 0.3);
        ">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="white">
            <polygon points="5 3 19 12 5 21 5 3"></polygon>
          </svg>
        </div>

        <h3 style="
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 12px;
          background: linear-gradient(135deg, var(--color-gold-light), var(--color-text-primary));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        ">${title}</h3>

        <p style="
          color: var(--color-text-secondary);
          font-size: 0.9rem;
          margin-bottom: 24px;
          line-height: 1.5;
        ">${message}</p>

        <div style="display: flex; flex-direction: column; gap: 12px;">
          <a href="#" class="popup-store-btn" style="
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            padding: 14px 20px;
            background: linear-gradient(135deg, #000, #1a1a1a);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            color: white;
            text-decoration: none;
            font-size: 0.9rem;
            font-weight: 500;
            transition: all 0.2s;
          ">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            <span>
              <span style="font-size: 0.7rem; opacity: 0.7; display: block;">Download on the</span>
              App Store
            </span>
          </a>

          <a href="#" class="popup-store-btn" style="
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            padding: 14px 20px;
            background: linear-gradient(135deg, #000, #1a1a1a);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            color: white;
            text-decoration: none;
            font-size: 0.9rem;
            font-weight: 500;
            transition: all 0.2s;
          ">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z"/>
            </svg>
            <span>
              <span style="font-size: 0.7rem; opacity: 0.7; display: block;">Get it on</span>
              Google Play
            </span>
          </a>
        </div>

        <p style="
          margin-top: 20px;
          font-size: 0.75rem;
          color: var(--color-text-secondary);
          opacity: 0.7;
        ">Free to download</p>
      </div>
    </div>

    <style>
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes slideUp {
        from { opacity: 0; transform: translateY(20px) scale(0.95); }
        to { opacity: 1; transform: translateY(0) scale(1); }
      }
      .popup-store-btn:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        border-color: var(--color-gold) !important;
      }
      #popup-close:hover {
        color: var(--color-text-primary);
      }
    </style>
  `;

  document.body.appendChild(popup);

  // Close handlers
  const closeBtn = document.getElementById('popup-close');
  const overlay = popup.querySelector('.popup-overlay');

  closeBtn?.addEventListener('click', () => popup.remove());
  overlay?.addEventListener('click', (e) => {
    if (e.target === overlay) {
      popup.remove();
    }
  });

  // Close on escape key
  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      popup.remove();
      document.removeEventListener('keydown', handleEscape);
    }
  };
  document.addEventListener('keydown', handleEscape);
}


/**
 * Draw waveform on canvas
 */
function drawWaveform(canvas: HTMLCanvasElement, peaks: number[]): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Set canvas size with device pixel ratio
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  ctx.scale(dpr, dpr);

  const width = rect.width;
  const height = rect.height;
  const barWidth = width / peaks.length;
  const centerY = height / 2;

  // Clear canvas
  ctx.clearRect(0, 0, width, height);

  // Draw bars
  ctx.fillStyle = 'rgba(167, 139, 250, 0.6)';

  peaks.forEach((peak, i) => {
    const barHeight = peak * height * 0.8;
    const x = i * barWidth;
    const y = centerY - barHeight / 2;

    // Draw bar with rounded corners
    ctx.beginPath();
    ctx.roundRect(x + 1, y, barWidth - 2, barHeight, 2);
    ctx.fill();
  });
}

/**
 * Generate placeholder waveform peaks
 */
function generatePlaceholderPeaks(count: number): number[] {
  const peaks: number[] = [];
  for (let i = 0; i < count; i++) {
    // Create a natural-looking waveform pattern
    const base = 0.3 + Math.random() * 0.3;
    const wave = Math.sin(i * 0.2) * 0.2;
    peaks.push(Math.max(0.1, Math.min(1, base + wave)));
  }
  return peaks;
}

/**
 * Format time in M:SS format
 */
function formatTime(seconds: number): string {
  if (isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
